<template>
  <div>
    <div v-if="error">{{ error.message }}</div>
    <div v-else>
      <p v-if="!data">Loading...</p>
      <div class="mx-auto max-w-screen-xl" v-else>
        <div v-if="data.display_landing_page">
          <section class="bg-white dark:bg-transparent">
            <div class="gap-8 items-center py-4 md:pt-0 mx-auto max-w-screen-xl xl:gap-16">
              <div>
                <BreadCrumbIndicator></BreadCrumbIndicator>

                <div class="flex lg:flex-row flex-col lg:space-x-10 px-1">
                  <div class="flex flex-col flex-grow mb-5 lg:mb-0">
                    <h1 class="text-4xl font-semibold">{{ data.title }}</h1>
                    <div v-if="data.top_description" class="hidden lg:block">
                      <p class="mt-6 ml-1">{{ data.top_description }}</p>
                      <br />
                      <span
                        v-if="headlessContent"
                        @click="scrollToGuide"
                        class="ml-1 float-left underline cursor-pointer"
                      >
                        {{ $t('Read Full Guide') }}
                      </span>
                      <br />
                      <br />
                    </div>
                  </div>
                  <div v-if="data.top_description" class="order-1 lg:hidden">
                    <p class="mt-6 ml-1">{{ data.top_description }}</p>
                    <br />
                    <span
                      v-if="headlessContent"
                      @click="scrollToGuide"
                      class="ml-1 float-left underline cursor-pointer"
                    >
                      {{ $t('Read Full Guide') }}
                    </span>
                    <br />
                    <br />
                  </div>
                  <SmartImg
                    v-if="data.featured_image"
                    :lazyLoadImg="false"
                    id="identity-image"
                    :src="data.featured_image"
                    :alt="data.title"
                    class="lg:w-2/5 w-full object-cover object-position-center mb-5 h-[350px] md:h-[350px] rounded-lg bg-gray-300 dark:bg-gray-800 order-0 lg:order-1 float-right"
                  />
                </div>
              </div>
            </div>
          </section>
          <div class="mt-8 md:mt-14" v-if="displayedIdentities.length == 0">
            <div class="overflow-x-auto whitespace-nowrap no-scrollbar pb-5 px-2 space-y-20" ref="scrollContainer">
              <ul>
                <SkeletonLoader class="h-10 w-2/3 mb-3"> </SkeletonLoader>
                <li v-for="item in [1, 2, 3, 4, 5, 6, 7, 8, 9, 10]" class="inline-block">
                  <SkeletonLoader class="w-[210px] h-[300px] md:w-[280px] md:h-[310px] mr-2" />
                </li>
              </ul>
            </div>
          </div>
          <div v-else v-for="(value, index) of displayedIdentities" :key="value.id">
            <div class="flex flex-col items-center">
              <Carousel
                :key="value.id"
                :title="value.name_alias"
                :itemType="'activity'"
                :items="value.activities"
                :isLoading="value.isLoading"
                class="my-10 md:my-2 md:py-8 mb-5 md:mb-0"
                :searchParams="value.searchParams"
                :URL="value.URL"
                :buttonLabel="value.name_alias"
                :class="{ 'bg-light-gray-100 dark:bg-transparent': index % 2 !== 0 }"
              />
            </div>
          </div>
          <div v-if="data.identities.length > displayedIdentities.length" class="flex justify-center mt-8">
            <button
              @click="showMore"
              class="bg-gradient-to-r from-ts_blue_to to-ts_blue_from text-white font-semibold py-2 px-4 rounded-full"
            >
              {{ $t('Show More') }}
            </button>
          </div>
        </div>
        <div v-else>
          <IdentityTop :data="data"></IdentityTop>
          <ActivitySearchAndFilter
            :initial-params="searchParams"
            @update:resultsCount="updateResultsCount"
            @error="handleError"
          />
        </div>
        <div class="py-9">
          <TopLinks v-if="data.top_links" :sections="data.top_links"></TopLinks>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, onMounted, ref } from 'vue';
import BreadCrumbIndicator from '~/components/BreadCrumbIndicator.vue';
import { saveItemInCookie } from '~/components/utils';
import { handleApiResponse } from '~/server/utils/utils';
import ActivitySearchAndFilter from '../components/ActivitiesSearchAndFilters.vue';

const route = useRoute();
const { locale } = useI18n();
const currentLang = locale.value;
const query = '';
const viewAllActivitiesURL = `/search?city_n=${encodeURIComponent(
  route.params.city_n,
)}&category_name=${encodeURIComponent(route.params.category_name)}&q=${query}`;
const emit = defineEmits(['passDataToHeader']);

const { data, error } = await handleApiResponse(`/api/explore`, {
  params: {
    type: 'category',
    city_name: route.params.city_n,
    category_name: route.params.category_name,
    lang: currentLang,
  },
});

// Save destination key in cookie to show the correct destination in the search bar and homepage
if (data.value) {
  emit('passDataToHeader', { popularSearches: data.value?.top_links, cityID: data?.value?.city?.destination_id, role: data?.value?.city?.role });
  // Save destination key in cookie to show the correct destination in the search bar and homepage
  saveItemInCookie('RVDestinations',data?.value?.category?.destination_key);
}

watch(
  (data) => {
    if (data?.value) {
      emit('passDataToHeader', { popularSearches: data.value?.top_links, cityID: data.value.city.destination_id });
      // Save destination key in cookie to show the correct destination in the search bar and homepage
      saveItemInCookie('RVDestinations', data?.value?.category?.destination_key);
    }
  },
  { immediate: true },
);


const searchParams = ref({
  city_name: data.value.search_params.cityName,
  filter_ID: data.value.search_params.filterID,
  lang: locale.value.split('-')[0],
  role: 'cat',
});

const resultsCount = ref(0);

function updateResultsCount(count) {
  resultsCount.value = count;
}

function handleError(error) {
  // console.error(error);
  // Handle the error appropriately
}

useHead({
  title: data?.value?.title,
  meta: [
    { name: 'title', content: data?.value?.meta_title },
    { name: 'description', content: data?.value?.meta_description },
    { name: 'keywords', content: data?.value?.meta_keywords },
    {
      name: 'robots',
      content: data?.value?.isIndexable
        ? 'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1'
        : 'noindex, follow',
    },
  ],
});

const rule = useRobotsRule();
rule.value = data?.value?.isIndexable
  ? 'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1'
  : 'noindex, follow';

useSeoMeta({
  ogType: 'website',
  title: data?.value?.meta_title,
  ogTitle: data?.value?.meta_title,
  description: data?.value?.meta_description,
  ogDescription: data?.value?.meta_description,
  ogImage: data?.value?.featured_image,
  keywords: data?.value?.meta_keywords,
  twitterCard: 'summary_large_image',
});

const displayCount = ref(5);
const loadedIdentities = ref([]);

const displayedIdentities = computed(() => {
  return loadedIdentities.value.slice(0, displayCount.value);
});

async function showMore() {
  const currentCount = loadedIdentities.value.length;
  const newCount = Math.min(currentCount + 5, data?.value?.identities.length ?? 0);

  for (let i = currentCount; i < newCount; i++) {
    const identity = data.value.identities[i];
    loadedIdentities.value.push({
      ...identity,
      activities: [],
      isLoading: true,
    });
  }

  displayCount.value = newCount;
}

onMounted(async () => {
  if (data.value && data.value.identities) {
    await showMore(); // Load the initial 5 identities
  }
});
</script>
